<script lang="ts">
import { usePrizesStore } from '@/stores/prizes'
import type { ProductOverview } from '@/types'
import type { TPlanTier } from '@/utils/helper-objects'
import { isBenefit, isPrize } from '@/utils/type-guard-utils'
import { benefitPlansTooltip } from '@/utils/utils'
import { type PropType, defineComponent } from 'vue'

/**
 * A card to display a product overview.
 *
 */
export default defineComponent({
  props: {
    product: {
      type: Object as PropType<ProductOverview>,
      required: true,
    },
  },
  setup() {
    return {
      benefitPlansTooltip,
      isBenefit,
      isPrize,
      prizesStore: usePrizesStore(),
    }
  },
  methods: {
    planBadgeColour(planTier?: TPlanTier): string {
      switch (planTier) {
        case 'vip':
          return 'bg-tertiary'

        case 'platinum':
          return 'bg-black'

        default:
          return 'bg-primary'
      }
    },
  },
})
</script>

<template>
  <div class="relative flex size-full flex-col bg-pure-white">
    <img
      :src="product.image.src"
      :alt="product.image.alt"
      class="w-full rounded-t-lg border border-b-0 border-slate-300"
    />

    <div
      class="flex flex-grow flex-col gap-2 rounded-b-lg border border-t-0 border-slate-300 px-5 py-6"
    >
      <div class="@sm/main:flex-1">
        <p class="text-lg font-extrabold">
          {{ product.title }}
        </p>

        <p class="line-clamp-3" :title="product.description">
          {{ product.description }}
        </p>
      </div>

      <div
        v-tooltip="{
          content: benefitPlansTooltip(product.plans),
          placement: 'bottom',
        }"
        class="my-3 flex flex-wrap items-baseline justify-between gap-2"
      >
        <div class="text-xs font-bold">Available with:</div>
        <div class="flex flex-wrap gap-2">
          <template v-if="product.plans.length">
            <AppBadge
              v-for="(plan, key) in prizesStore.productPlans(product)"
              :key
              size="md"
              class="uppercase text-white"
              :class="planBadgeColour(plan.tier)"
            >
              {{ $t('membership_badge.' + plan.tier.toLocaleLowerCase()) }}
            </AppBadge>
          </template>
          <template v-if="!product.plans.length">
            <AppBadge
              size="md"
              class="uppercase text-white"
              :class="planBadgeColour()"
            >
              All
            </AppBadge>
          </template>
        </div>
      </div>

      <AppButton
        v-if="isPrize(product)"
        :route="{ name: 'prizes' }"
        class="min-w-full mt-auto font-semibold whitespace-nowrap !leading-5"
        :class="
          $userStore.isLoggedIn
            ? 'button-primary'
            : 'button-secondary button-secondary-light'
        "
        title="View prizes"
        :icon="Icons.ArrowRight"
      >
        View prizes
      </AppButton>

      <SectionGuard section="memberships">
        <AppButton
          v-if="isBenefit(product)"
          :route="{ name: $userStore.isLoggedIn ? 'benefits' : 'memberships' }"
          class="mt-auto min-w-full font-semibold whitespace-nowrap !leading-5"
          :class="
            $userStore.isLoggedIn
              ? 'button-primary'
              : 'button-secondary button-secondary-light'
          "
          :title="$userStore.isLoggedIn ? 'Claim' : 'Join to claim'"
          :icon="Icons.ArrowRight"
        >
          {{ $userStore.isLoggedIn ? 'View benefits' : 'Join to claim' }}
        </AppButton>
      </SectionGuard>
    </div>
  </div>
</template>
